import { createContext, FC, useState, useEffect, useContext } from 'react';

import { useParams } from 'react-router-dom';
import { useRealmMetadataQuery } from '~/queries/useAuthQuery';

import { AuthClient, AuthClientOpts } from './clients';
import { createAuthClient } from './createAuthClient';

export type AuthContext = {
  client: AuthClient;
  loading?: boolean;
  isAuthenticated?: boolean;
  targetUrl: string;
  logout: (params: { returnTo: string }) => void;
};

export type { AuthClientOpts };

export const AuthContext = createContext<AuthContext | null>(null);
export const useAuth = () => useContext(AuthContext)!;

type Props = {
  children: JSX.Element;
  loading?: boolean;
  authOptions: AuthClientOpts;
};

let client: AuthClient;

// XXX this is bad: we need to access token in string utils and websockets, we should follow a more react-style
// just keep it because some code depends on it, but we should refactor it as well
export const getTokenSilently = async (params: any) => client?.getTokenSilently(params);

export const AuthProvider: FC<Props> = ({ children, authOptions }) => {
  const { logout } = useParams<'logout'>();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [targetUrl, setTargetUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const { isFetching: isFetchingMetadata, data: enhancedRealmMetadata } = useRealmMetadataQuery();

  useEffect(() => {
    const tryLogin = async () => {
      if (!client && enhancedRealmMetadata) {
        client = await createAuthClient(authOptions, enhancedRealmMetadata);
      }
      try {
        // attempt to login
        await client.login();

        const isLoggedIn = await client.isAuthenticated();
        const target = client.getTargetUrl();

        // this is the target where we should redirected to after successful login
        if (target) {
          setTargetUrl(target);
        }

        setIsAuthenticated(isLoggedIn);
        setLoading(false);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log('Authentication error', e);

        setIsAuthenticated(false);
        setLoading(false);

        // we call logout in order to clean up temporary values created while attempting login

        // after logout we are redirected back to this url
        // unfortunately it is not possible in Auth0 to avoid the redirection, see https://github.com/auth0/auth0.js/issues/618
        // so here we need to hack together a solution to avoid an infinite loop here -> signout-callback -> here -> ...
        // in signout-callback we append a logout param to url
        const returnTo = `${window.location.protocol}//${window.location.host}/signout-callback`;
        const isAlreadyLoggedOut = logout === 'true';

        if (isAlreadyLoggedOut) {
          await client.logout({ returnTo });
        }
      }
    };

    if (!isAuthenticated && !isFetchingMetadata) {
      // We only need to login if not yet authenticated.
      tryLogin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authOptions, isAuthenticated, logout, isFetchingMetadata]);

  return (
    <AuthContext.Provider
      value={{
        client,
        isAuthenticated,
        targetUrl,
        loading,
        logout: (params) => client.logout(params),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
